// Master unit converter — works on any factor-based category and special-cases temperature + data.

import { LENGTH_UNITS, LENGTH_UNIT_MAP, type LengthUnitKey } from "@/data/lengthUnits";
import { WEIGHT_UNITS, WEIGHT_UNIT_MAP, type WeightUnitKey } from "@/data/weightUnits";
import { LAND_UNITS, UNITS_BY_ID as LAND_UNIT_MAP, type LandUnit } from "@/data/bdLandUnits";
import { VOLUME_UNITS, type VolumeUnit, type VolumeUnitKey } from "@/data/volumeUnits";
import { TIME_UNITS, type TimeUnit, type TimeUnitKey } from "@/data/timeUnits";

/* ---------- Generic factor-based conversion ---------- */

export interface FactorUnit {
  key: string;
  en: string;
  bn: string;
  shortEn: string;
  shortBn: string;
  factor: number; // 1 unit = X base units
  note?: { en: string; bn: string };
}

function asFactor<T extends { key: string; en: string; bn: string; shortEn?: string; shortBn?: string; symbol?: string; note?: { en: string; bn: string } }>(
  list: T[],
  read: (u: T) => number,
  fallbackShortEn?: (u: T) => string,
  fallbackShortBn?: (u: T) => string,
): FactorUnit[] {
  return list.map((u) => ({
    key: u.key,
    en: u.en,
    bn: u.bn,
    shortEn: u.shortEn ?? u.symbol ?? fallbackShortEn?.(u) ?? u.en,
    shortBn: u.shortBn ?? u.symbol ?? fallbackShortBn?.(u) ?? u.bn,
    factor: read(u),
    note: u.note,
  }));
}

const LENGTH = asFactor(LENGTH_UNITS, (u) => u.meters);
const WEIGHT = asFactor(WEIGHT_UNITS, (u) => u.kg);
const VOLUME = asFactor(VOLUME_UNITS, (u) => u.liters);
const TIME   = asFactor(TIME_UNITS,   (u) => u.seconds);

const AREA: FactorUnit[] = LAND_UNITS.map((u: LandUnit) => ({
  key: u.id,
  en: u.en,
  bn: u.bn,
  shortEn: u.symbol ?? u.id,
  shortBn: u.symbol ?? u.bn,
  factor: u.sqft, // base = sq ft
  note: u.note,
}));

const SPEED: FactorUnit[] = [
  // base = m/s
  { key: "ms",   en: "Meter / second",      bn: "মিটার/সেকেন্ড",     shortEn: "m/s",   shortBn: "মি/সে",   factor: 1 },
  { key: "kmh",  en: "Kilometer / hour",    bn: "কিমি/ঘণ্টা",         shortEn: "km/h",  shortBn: "কিমি/ঘ", factor: 1 / 3.6 },
  { key: "mph",  en: "Mile / hour",         bn: "মাইল/ঘণ্টা",         shortEn: "mph",   shortBn: "মাইল/ঘ", factor: 0.44704 },
  { key: "fts",  en: "Foot / second",       bn: "ফুট/সেকেন্ড",        shortEn: "ft/s",  shortBn: "ফুট/সে", factor: 0.3048 },
  { key: "knot", en: "Knot",                bn: "নট",                  shortEn: "kn",    shortBn: "নট",     factor: 0.514444,
    note: { en: "Nautical: 1 knot = 1 nautical mile / hour.", bn: "নটিক্যাল: ১ নট = ১ নটিক্যাল মাইল/ঘণ্টা।" } },
];

const ENERGY: FactorUnit[] = [
  // base = joule
  { key: "j",   en: "Joule",       bn: "জুল",        shortEn: "J",    shortBn: "জুল",      factor: 1 },
  { key: "kj",  en: "Kilojoule",   bn: "কিলোজুল",   shortEn: "kJ",   shortBn: "কিজুল",    factor: 1000 },
  { key: "cal", en: "Calorie",     bn: "ক্যালরি",   shortEn: "cal",  shortBn: "ক্যাল.",   factor: 4.184 },
  { key: "kcal",en: "Kilocalorie", bn: "কিলোক্যালরি",shortEn: "kcal",shortBn: "কিক্যাল.",factor: 4184 },
  { key: "wh",  en: "Watt-hour",   bn: "ওয়াট-ঘণ্টা",shortEn: "Wh",   shortBn: "ওঘ",      factor: 3600 },
  { key: "kwh", en: "Kilowatt-hour",bn: "কিলোওয়াট-ঘণ্টা",shortEn:"kWh",shortBn:"কিওঘ", factor: 3600000 },
  { key: "btu", en: "BTU",         bn: "বিটিইউ",    shortEn: "BTU",  shortBn: "বিটিইউ",   factor: 1055.06 },
];

const PRESSURE: FactorUnit[] = [
  // base = pascal
  { key: "pa",   en: "Pascal",      bn: "প্যাস্কাল",    shortEn: "Pa",   shortBn: "Pa",    factor: 1 },
  { key: "kpa",  en: "Kilopascal",  bn: "কিলোপ্যাস্কাল",shortEn:"kPa",  shortBn: "kPa",   factor: 1000 },
  { key: "bar",  en: "Bar",         bn: "বার",          shortEn: "bar",  shortBn: "বার",   factor: 100000 },
  { key: "atm",  en: "Atmosphere",  bn: "অ্যাটমোস্ফিয়ার",shortEn:"atm", shortBn: "atm",   factor: 101325 },
  { key: "psi",  en: "PSI",         bn: "পিএসআই",       shortEn: "psi",  shortBn: "psi",   factor: 6894.76 },
  { key: "mmhg", en: "mm of Mercury",bn: "মিমি পারদ",   shortEn: "mmHg", shortBn: "mmHg",  factor: 133.322,
    note: { en: "Used in blood pressure readings.", bn: "রক্তচাপ পরিমাপে ব্যবহৃত।" } },
];

/* ---------- Data storage (binary vs decimal) ---------- */

export type DataMode = "decimal" | "binary";

export const DATA_UNITS_DECIMAL: FactorUnit[] = [
  // base = byte
  { key: "B",  en: "Byte",      bn: "বাইট",        shortEn: "B",  shortBn: "B",  factor: 1 },
  { key: "KB", en: "Kilobyte",  bn: "কিলোবাইট",   shortEn: "KB", shortBn: "KB", factor: 1e3 },
  { key: "MB", en: "Megabyte",  bn: "মেগাবাইট",   shortEn: "MB", shortBn: "MB", factor: 1e6 },
  { key: "GB", en: "Gigabyte",  bn: "গিগাবাইট",   shortEn: "GB", shortBn: "GB", factor: 1e9 },
  { key: "TB", en: "Terabyte",  bn: "টেরাবাইট",   shortEn: "TB", shortBn: "TB", factor: 1e12 },
  { key: "PB", en: "Petabyte",  bn: "পেটাবাইট",   shortEn: "PB", shortBn: "PB", factor: 1e15 },
  { key: "bit",en: "Bit",       bn: "বিট",         shortEn: "bit",shortBn: "bit",factor: 0.125 },
];

export const DATA_UNITS_BINARY: FactorUnit[] = [
  { key: "B",   en: "Byte",      bn: "বাইট",       shortEn: "B",   shortBn: "B",   factor: 1 },
  { key: "KiB", en: "Kibibyte",  bn: "কিবিবাইট",  shortEn: "KiB", shortBn: "KiB", factor: 1024 },
  { key: "MiB", en: "Mebibyte",  bn: "মেবিবাইট",  shortEn: "MiB", shortBn: "MiB", factor: 1024 ** 2 },
  { key: "GiB", en: "Gibibyte",  bn: "গিবিবাইট",  shortEn: "GiB", shortBn: "GiB", factor: 1024 ** 3 },
  { key: "TiB", en: "Tebibyte",  bn: "টেবিবাইট",  shortEn: "TiB", shortBn: "TiB", factor: 1024 ** 4 },
  { key: "PiB", en: "Pebibyte",  bn: "পেবিবাইট",  shortEn: "PiB", shortBn: "PiB", factor: 1024 ** 5 },
  { key: "bit", en: "Bit",       bn: "বিট",        shortEn: "bit", shortBn: "bit", factor: 0.125 },
];

/* ---------- Categories ---------- */

export type CategoryKey =
  | "length" | "weight" | "area" | "volume"
  | "temperature" | "time" | "speed" | "data" | "energy" | "pressure";

export interface Category {
  key: CategoryKey;
  en: string;
  bn: string;
  units: FactorUnit[]; // empty for temperature; data uses live based on mode
  defaultFrom: string;
  defaultTo: string;
  defaultAmount: string;
  /** Cross-link slug for a dedicated page (if any). */
  related?: { to: string; en: string; bn: string };
}

export const CATEGORIES: Record<CategoryKey, Category> = {
  length:      { key: "length",      en: "Length",      bn: "দৈর্ঘ্য",  units: LENGTH,  defaultFrom: "km",   defaultTo: "mile", defaultAmount: "100",
    related: { to: "/feet-to-meter", en: "Feet ↔ Meter", bn: "ফুট ↔ মিটার" } },
  weight:      { key: "weight",      en: "Weight",      bn: "ওজন",      units: WEIGHT,  defaultFrom: "kg",   defaultTo: "lb",   defaultAmount: "1",
    related: { to: "/kg-to-pounds", en: "KG ↔ Pounds", bn: "কেজি ↔ পাউন্ড" } },
  area:        { key: "area",        en: "Area",        bn: "ক্ষেত্রফল", units: AREA,    defaultFrom: "katha",defaultTo: "sqft", defaultAmount: "1",
    related: { to: "/land-calculator", en: "Land Calculator", bn: "জমি ক্যালকুলেটর" } },
  volume:      { key: "volume",      en: "Volume",      bn: "আয়তন",    units: VOLUME,  defaultFrom: "l",    defaultTo: "galUS",defaultAmount: "1" },
  temperature: { key: "temperature", en: "Temperature", bn: "তাপমাত্রা",units: [],      defaultFrom: "C",    defaultTo: "F",    defaultAmount: "37" },
  time:        { key: "time",        en: "Time",        bn: "সময়",     units: TIME,    defaultFrom: "day",  defaultTo: "s",    defaultAmount: "1" },
  speed:       { key: "speed",       en: "Speed",       bn: "গতি",     units: SPEED,   defaultFrom: "kmh",  defaultTo: "mph",  defaultAmount: "100",
    related: { to: "/distance-calculator", en: "Distance Calculator", bn: "দূরত্ব ক্যালকুলেটর" } },
  data:        { key: "data",        en: "Data",        bn: "ডেটা",     units: DATA_UNITS_DECIMAL, defaultFrom: "GB", defaultTo: "MB", defaultAmount: "1" },
  energy:      { key: "energy",      en: "Energy",      bn: "শক্তি",   units: ENERGY,  defaultFrom: "kcal", defaultTo: "kj",   defaultAmount: "1" },
  pressure:    { key: "pressure",    en: "Pressure",    bn: "চাপ",     units: PRESSURE,defaultFrom: "atm",  defaultTo: "psi",  defaultAmount: "1" },
};

export const CATEGORY_ORDER: CategoryKey[] = [
  "length", "weight", "area", "volume", "temperature",
  "time", "speed", "data", "energy", "pressure",
];

/* ---------- Conversion functions ---------- */

export function convertFactor(value: number, from: FactorUnit | undefined, to: FactorUnit | undefined): number {
  if (!from || !to || !Number.isFinite(value)) return NaN;
  const base = value * from.factor;
  return base / to.factor;
}

export function findUnit(units: FactorUnit[], key: string): FactorUnit | undefined {
  return units.find((u) => u.key === key);
}

export function convertAll(value: number, from: FactorUnit | undefined, units: FactorUnit[]): { unit: FactorUnit; value: number }[] {
  if (!from || !Number.isFinite(value)) return [];
  const base = value * from.factor;
  return units.map((u) => ({ unit: u, value: base / u.factor }));
}

/* ---------- Temperature (non-linear) ---------- */

export type TempUnit = "C" | "F" | "K";

export function convertTemperature(value: number, from: TempUnit, to: TempUnit): number {
  if (!Number.isFinite(value)) return NaN;
  // → Celsius
  let c: number;
  if (from === "C") c = value;
  else if (from === "F") c = (value - 32) * 5 / 9;
  else c = value - 273.15; // K
  // Celsius → target
  if (to === "C") return c;
  if (to === "F") return c * 9 / 5 + 32;
  return c + 273.15;
}

export const TEMP_UNITS: { key: TempUnit; en: string; bn: string; symbol: string }[] = [
  { key: "C", en: "Celsius",    bn: "সেলসিয়াস",  symbol: "°C" },
  { key: "F", en: "Fahrenheit", bn: "ফারেনহাইট",   symbol: "°F" },
  { key: "K", en: "Kelvin",     bn: "কেলভিন",      symbol: "K" },
];

/* ---------- Re-exports ---------- */

export {
  LENGTH_UNIT_MAP, WEIGHT_UNIT_MAP, LAND_UNIT_MAP,
  VOLUME_UNITS, TIME_UNITS,
};
export type { LengthUnitKey, WeightUnitKey, VolumeUnitKey, TimeUnitKey, VolumeUnit, TimeUnit };
